<script setup lang="ts">
import { INQUIRY_STATUS_OPTION, INQUIRY_STATUS, INQUIRY_ITEM } from '@/types'
import {
  matchInquiryStatusToCssVariable,
  matchInquiryStatusToIcon,
  matchInquiryTypeToStatusList,
  matchAllowedDDInquiryStatuses,
} from '@/utils/matchers'

const emits = defineEmits(['update:status', 'update:popup-open'])
const appStore = useAppStore()

const props = defineProps({
  selectedStatus: {
    type: String as PropType<INQUIRY_STATUS>,
    required: false,
  },
  as: {
    type: String as PropType<'button' | 'quickActionButton'>,
    default: 'button',
  },
  inquiryId: {
    type: Number,
    required: false,
  },
  autoResponsePopupOpen: {
    type: Boolean,
    default: false,
    required: false,
  },
  size: {
    type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl'>,
    default: 'md',
    required: false,
  },
})

const route = useRoute()
const inquiriesStore = useInquiriesStore()
const selectionDisabled = ref(false)

const inquiry = inquiriesStore.getInquiryById(
  Number(route.query.inquiry_id ?? props.inquiryId),
)!

const inquiryStatusOptions = shallowRef<INQUIRY_STATUS_OPTION[]>(
  matchInquiryTypeToStatusList(inquiry.type).map((status) => ({
    label: status as string,
    variant: matchInquiryStatusToCssVariable(status),
    icon: matchInquiryStatusToIcon(status),
  })) as INQUIRY_STATUS_OPTION[],
)

if (inquiry.is_spothopper_delivery) {
  inquiryStatusOptions.value = matchAllowedDDInquiryStatuses(
    inquiry.delivery_order_status,
    inquiryStatusOptions.value,
  )
} else {
  const filtered = inquiryStatusOptions.value.filter(
    (option: INQUIRY_STATUS_OPTION) =>
      option.label !== 'accepted, arrange pickup',
  )
  inquiryStatusOptions.value = filtered
}

if (
  inquiry.payment_status &&
  inquiry.delivery_order_status != 'received' &&
  inquiry.pick_up_order_status == null
) {
  const filtered = inquiryStatusOptions.value.filter(
    (option: INQUIRY_STATUS_OPTION) => option.label !== 'received',
  )
  inquiryStatusOptions.value = filtered
}

if (
  inquiry.is_pos_integration &&
  inquiry.is_order &&
  !inquiry.is_spothopper_delivery
) {
  if (
    inquiry.pick_up_order_status === 'received' ||
    inquiry.delivery_order_status === 'received'
  ) {
    const filtered = inquiryStatusOptions.value.filter(
      (option: INQUIRY_STATUS_OPTION) =>
        option.label === 'received' || option.label === 'rejected',
    )
    inquiryStatusOptions.value = filtered
  }
}
if(inquiry.delivery_order_status != 'received' && inquiry.pick_up_order_status != 'received')
  {
    inquiryStatusOptions.value = inquiryStatusOptions.value.filter(
      (option: INQUIRY_STATUS_OPTION) => option.label !== 'received',
    )
  }
if (
  (inquiry.pick_up_order_status === 'rejected' ||
    inquiry.delivery_order_status === 'rejected') &&
  inquiry.payment_status
) {
  selectionDisabled.value = true
}

const findStatusOptionByLabel = (label: string) =>
  inquiryStatusOptions.value.find((option) => option.label === label)

const showInquiryStatusPopUp = ref(false)

watch(showInquiryStatusPopUp, (newValue) => {
  emits('update:popup-open', newValue)
})

const selectedInquiryStatus = shallowRef(
  props.selectedStatus
    ? findStatusOptionByLabel(props.selectedStatus)
    : inquiryStatusOptions.value[0],
)

const handleSelectInquiryStatus = (option: INQUIRY_STATUS_OPTION) => {
  selectedInquiryStatus.value = option
  showInquiryStatusPopUp.value = false
  inquiriesStore.setSelectedInquiryStatus(undefined)
  setTimeout(() => {
    inquiriesStore.setSelectedInquiryStatus(option.label)
  }, 200)

  if (
    option.label === 'rejected' &&
    inquiry.is_pos_integration &&
    inquiry.is_order
  ) {
    selectionDisabled.value = true
  }

  if (option.label === 'rejected' && inquiry.payment_status) {
    selectionDisabled.value = true
  }

  if (
    option.label === 'received' &&
    inquiry.is_pos_integration &&
    !inquiry.is_spothopper_delivery
  ) {
    const filtered = inquiryStatusOptions.value.filter(
      (option: INQUIRY_STATUS_OPTION) =>
        option.label === 'received' || option.label === 'rejected',
    )
    inquiryStatusOptions.value = filtered
  }

  emits('update:status', option.label)
}

watch(inquiry, (val: INQUIRY_ITEM) => {
  if (!val) return

  if (
    inquiry.payment_status &&
    val.delivery_order_status != 'received' &&
    val.pick_up_order_status == null
  ) {
    const filtered = inquiryStatusOptions.value.filter(
      (option: INQUIRY_STATUS_OPTION) => option.label !== 'received',
    )
    inquiryStatusOptions.value = filtered
  }
})

watch(
  () => props.selectedStatus,
  (val) => {
    if (!val) return
    selectedInquiryStatus.value = findStatusOptionByLabel(val)
  },
)

watch(
  () => inquiriesStore.selectedInquiryStatus,
  (val) => {
    if (!val) return
    if (
      inquiriesStore.isInquiryStatusAutomaticChange &&
      props.autoResponsePopupOpen
    ) {
      inquiriesStore.autoResponseStatus = val
      return
    }
    inquiriesStore.autoResponseStatus = undefined
    selectedInquiryStatus.value = findStatusOptionByLabel(val)
    if (
      (val !== 'rejected' && inquiry.is_order && inquiry.is_pos_integration) ||
      (val !== 'rejected' && inquiry.payment_status)
    ) {
      selectionDisabled.value = false
    }
  },
)
</script>

<template>
  <template v-if="props.as == 'quickActionButton'">
    <div
      class="flex flex-col items-center justify-center"
      @click="showInquiryStatusPopUp = true"
    >
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20"
      >
        <AppiconClock class="h-5 w-5 fill-white" />
      </div>
      <span class="text-sm text-white">Change Status</span>
    </div>
  </template>
  <template v-else>
    <AppButton
      v-if="selectedInquiryStatus?.variant"
      type="button"
      variant="secondary"
      class="w-4 min-w-[13rem] !gap-1 !bg-[var(--color)] text-center !ring-offset-0"
      :size="props.size"
      :is-rounded="true"
      :is-wide="true"
      :style="{
        '--color': `hsl(var(--color-status-${selectedInquiryStatus!.variant}))`,
      }"
      :class="{
        'font-normal !opacity-80': selectionDisabled,
      }"
      :disabled="selectionDisabled"
      @click="showInquiryStatusPopUp = true"
    >
      <Component :is="selectedInquiryStatus!.icon" class="h-6 w-6" />
      <span class="capitalize">
        {{ selectedInquiryStatus!.label }}
      </span>
    </AppButton>
    <AppButton
      v-else
      type="button"
      variant="secondary"
      class="w-4 min-w-[13rem] !gap-1 !bg-[var(--color)] text-center !ring-offset-0"
      :size="props.size"
      :is-rounded="true"
      :is-wide="true"
      :style="{
        '--color': `hsl(var(--color-status-unconfirmed))`,
      }"
      :class="{
        'border-white !bg-transparent font-normal text-white !opacity-30':
          selectionDisabled,
      }"
      :disabled="selectionDisabled"
      @click="showInquiryStatusPopUp = true"
    >
      <span class="capitalize">
        {{ props.selectedStatus }}
      </span>
    </AppButton>
  </template>
  <AppDialog
    :is-open="showInquiryStatusPopUp"
    :can-close="true"
    :has-close-button="true"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="showInquiryStatusPopUp = false"
  >
    <template #title>
      <p class="font-semibold text-gray-500">Set Status To</p>
    </template>
    <template #content>
      <AppList
        v-for="option in inquiryStatusOptions.filter(
          (option: INQUIRY_STATUS_OPTION) =>
            option.label !== 'autoresponse sent',
        )"
        :key="option.label as string"
        name="filter"
        :checked="option.label === selectedInquiryStatus?.label"
        :label="option.label as string"
        :icon="option.icon"
        :show-radio-input="false"
        icon-classes="fill-[var(--color-status)] w-6"
        :icon-variant="option.variant"
        :capitalize="true"
        :is-desktop="appStore.spotHopperDesktopDetailsPage"
        @click="handleSelectInquiryStatus(option)"
      />
    </template>
  </AppDialog>
</template>
